import React from 'react';
import Nav from "react-bootstrap/Nav";
import Dropdown from "react-bootstrap/Dropdown";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { checkInJob, selectIsCurrentUserOwner } from "../../redux/slices/checkoutSlice"; // Import the actions
import {
  selectUserDetails,
} from "../../redux/slices/userSlice";
import { PRODUCTION_PORTAL_PATH } from "../../config/productionRoutes";

const JOB_NAV_ITEMS = [
  {
    eventKey: "generalInfo",
    path: "generalInfo",
    label: "General",
    icon: "bi-info-circle",
    tbdPage: "General",
  },
  {
    eventKey: "salesInfo",
    path: "salesInfo",
    label: "Sales",
    icon: "bi-graph-up",
    tbdPage: "Sales Information",
  },
  {
    eventKey: "mechInfo",
    path: "mechInfo",
    label: "Mechanical",
    icon: "bi-gear",
    tbdPage: "Mechanical",
  },
  {
    eventKey: "elecInfo",
    path: "elecInfo",
    label: "Electrical",
    icon: "bi-lightning",
    tbdPage: "Electrical",
  },
  {
    eventKey: "changeOrders",
    path: "changeOrders",
    label: "Change Orders",
    icon: "bi-file-earmark-text",
  },
  {
    eventKey: "jobQa",
    path: "jobQa",
    label: "Job QA",
    icon: "bi-check-square",
  },
  {
    eventKey: "additionalItems",
    path: "additionalItems",
    label: "Additional Items",
    icon: "bi-plus-square",
  },
  {
    eventKey: "forms",
    path: "forms/apps",
    label: "Forms",
    icon: "bi-file-earmark-text",
  },
];

function JobNav({ keyValue }) {
  const userDetails = useSelector(selectUserDetails); // User details
  const jobId = useSelector((state) => state.jobId.jobId); // Access the stored jobId
  const tbdForms = useSelector((state) => state.tbd?.forms || {});
  const isCurrentUserOwner = useSelector(selectIsCurrentUserOwner); // Check if current user owns checkout

  const navigate = useNavigate(); // Use navigate for navigation
  const dispatch = useDispatch(); // Set up dispatch for redux actions
  const currentUser = userDetails?.name;

  // Handle the "Home" button click
  const handleHomeClick = (e) => {
    e.preventDefault(); // Prevent the default navigation behavior

    if (jobId && isCurrentUserOwner) {
      console.log(`Checking in job with ID: ${jobId}`); // Debugging log

      // Only dispatch checkInJob if current user owns the checkout
      dispatch(checkInJob({ jobId, currentUser }))
        .then(() => {
          console.log("Job checked in successfully");
          navigate(PRODUCTION_PORTAL_PATH); // Navigate to home after checking in the job
        })
        .catch((error) => {
          console.error("Failed to check in the job", error);
          navigate(PRODUCTION_PORTAL_PATH); // Still navigate home if there's an error
        });
    } else {
      navigate(PRODUCTION_PORTAL_PATH); // If no jobId or user doesn't own checkout, just navigate to home
    }
  };

  // Helper function to check if a page has TBD for the current jobId
  const hasTbdForPage = (pageName) => {
    if (pageName === "General") {
      return Boolean(tbdForms.generalProject?.hasTbd || tbdForms.generalStation?.hasTbd);
    }

    if (pageName === "Sales Information") {
      return Boolean(tbdForms.soldToShipTo?.hasTbd || tbdForms.priceBreakdown?.hasTbd || tbdForms.ownersManual?.hasTbd);
    }

    if (pageName === "Mechanical") {
      return Boolean(
        tbdForms.hcRequirements?.hasTbd ||
        tbdForms.skidConstruction?.hasTbd ||
        tbdForms.pumpManifoldPiping?.hasTbd ||
        tbdForms.stationEnclosure?.hasTbd ||
        tbdForms.dropPipeComments?.hasTbd ||
        tbdForms.dropPipes?.hasTbd ||
        tbdForms.levelControls?.hasTbd ||
        tbdForms.fillSources?.hasTbd ||
        tbdForms.pumps?.hasTbd ||
        tbdForms.pumpDetails?.hasTbd ||
        tbdForms.waterTreatment?.hasTbd ||
        tbdForms.inletScreen?.hasTbd ||
        tbdForms.inletStrainer?.hasTbd ||
        tbdForms.dischargeFilter?.hasTbd ||
        tbdForms.dischargeStrainer?.hasTbd ||
        tbdForms.roSystem?.hasTbd ||
        tbdForms.uvSystem?.hasTbd ||
        tbdForms.fertigation?.hasTbd ||
        tbdForms.skyHarvester?.hasTbd ||
        tbdForms.shipLoose?.hasTbd ||
        tbdForms.shipEarlyParent?.hasTbd ||
        tbdForms.shipEarlyItems?.hasTbd
      );
    }

    if (pageName === "Electrical") {
      return Boolean(
        tbdForms.electricalOptions?.hasTbd ||
        tbdForms.telemetry?.hasTbd ||
        tbdForms.telemetryAccessories?.hasTbd ||
        tbdForms.unregulatedPower?.hasTbd ||
        tbdForms.unregulatedBreakers?.hasTbd ||
        tbdForms.regulatedPower?.hasTbd ||
        tbdForms.regulatedBreakers?.hasTbd ||
        tbdForms.sequenceOfOperations?.hasTbd ||
        tbdForms.controlLogicOnly?.hasTbd
      );
    }

    return false;
  };

  const activeMobileItem =
    JOB_NAV_ITEMS.find((item) => item.eventKey === keyValue) || JOB_NAV_ITEMS[0];

  return (
    <div className="modern-nav-container job-nav-container">
      <Nav
        variant="tabs"
        activeKey={keyValue}
        className="modern-nav job-nav job-nav-desktop"
        aria-label="Job sections"
      >
        <Nav.Item>
          <Nav.Link 
            onClick={handleHomeClick}
            className="modern-nav-link home-nav-link"
            aria-label="Back to all jobs"
            title="Back to all jobs"
          >
            <i className="bi bi-arrow-left" aria-hidden="true"></i>
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link 
            as={Link}
            to={`/${jobId}/generalInfo`}
            eventKey="generalInfo"
            className="modern-nav-link"
          >
            <i className="bi bi-info-circle me-2"></i>
            General
            {hasTbdForPage("General") && (
              <i className="bi bi-exclamation-lg ms-1 text-danger" />
            )}
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link 
            as={Link}
            to={`/${jobId}/salesInfo`}
            eventKey="salesInfo"
            className="modern-nav-link"
          >
            <i className="bi bi-graph-up me-2"></i>
            Sales
            {hasTbdForPage("Sales Information") && (
              <i className="bi bi-exclamation-lg ms-1 text-danger" />
            )}
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link 
            as={Link}
            to={`/${jobId}/mechInfo`}
            eventKey="mechInfo"
            className="modern-nav-link"
          >
            <i className="bi bi-gear me-2"></i>
            Mechanical
            {hasTbdForPage("Mechanical") && (
              <i className="bi bi-exclamation-lg ms-1 text-danger" />
            )}
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link 
            as={Link}
            to={`/${jobId}/elecInfo`}
            eventKey="elecInfo"
            className="modern-nav-link"
          >
            <i className="bi bi-lightning me-2"></i>
            Electrical
            {hasTbdForPage("Electrical") && (
              <i className="bi bi-exclamation-lg ms-1 text-danger" />
            )}
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link 
            as={Link}
            to={`/${jobId}/changeOrders`}
            eventKey="changeOrders"
            className="modern-nav-link"
          >
            <i className="bi bi-file-earmark-text me-2"></i>
            Change Orders
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link 
            as={Link}
            to={`/${jobId}/jobQa`}
            eventKey="jobQa"
            className="modern-nav-link"
          >
            <i className="bi bi-check-square me-2"></i>
            Job QA
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link 
            as={Link}
            to={`/${jobId}/additionalItems`}
            eventKey="additionalItems"
            className="modern-nav-link"
          >
            <i className="bi bi-plus-square me-2"></i>
            Additional Items
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link as={Link} to={`/${jobId}/forms/apps`} eventKey="forms" className="modern-nav-link">
            <i className="bi bi-file-earmark-text me-2"></i>
            Forms
          </Nav.Link>
        </Nav.Item>
      </Nav>

      <Dropdown className="job-nav-mobile">
        <Dropdown.Toggle
          id="job-nav-mobile-menu"
          className="job-nav-mobile-toggle"
          aria-label="Choose job section"
        >
          <span className="job-nav-mobile-current">
            <i className={`bi ${activeMobileItem.icon} me-2`}></i>
            {activeMobileItem.label}
            {activeMobileItem.tbdPage && hasTbdForPage(activeMobileItem.tbdPage) && (
              <i className="bi bi-exclamation-lg ms-1 text-danger" />
            )}
          </span>
        </Dropdown.Toggle>

        <Dropdown.Menu>
          <Dropdown.Item
            as="button"
            onClick={handleHomeClick}
            className="job-nav-mobile-item job-nav-mobile-home"
            aria-label="Back to all jobs"
            title="Back to all jobs"
          >
            <i className="bi bi-arrow-left" aria-hidden="true"></i>
          </Dropdown.Item>
          <Dropdown.Divider />
          {JOB_NAV_ITEMS.map((item) => (
            <Dropdown.Item
              key={item.eventKey}
              as={Link}
              to={`/${jobId}/${item.path}`}
              active={keyValue === item.eventKey}
              className="job-nav-mobile-item"
            >
              <i className={`bi ${item.icon} me-2`}></i>
              {item.label}
              {item.tbdPage && hasTbdForPage(item.tbdPage) && (
                <i className="bi bi-exclamation-lg ms-1 text-danger" />
              )}
            </Dropdown.Item>
          ))}
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );
}

export default JobNav;
